/**
 * Helpers voor het authed (same-origin proxy) ophalen, previewen en direct
 * downloaden van documenten/PDF's.
 *
 * Kern-probleem dat dit oplost: een `<a href>` naar een authed API-endpoint
 * draagt de Bearer-token niet betrouwbaar mee en opent het bestand in een
 * nieuw venster/viewer (waarna je nogmaals moet downloaden). De oplossing:
 * haal het bestand via een geauthenticeerde fetch op (de URL is altijd een
 * same-origin proxy-route, dus de Auth.js-sessiecookie reist mee), maak er een
 * blob van, en gebruik die voor preview (object-URL) of een directe download
 * (programmatic `<a download>`). Daarom `credentials: 'include'` en GEEN
 * Bearer-header: client-side `apiFetch` tegen de cross-origin API mag niet
 * (zie CLAUDE.md) - de proxy-route zet de token server-side.
 */

export type DocumentKind = 'image' | 'pdf' | 'other'

const EXT_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
}

const TYPE_EXT: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'application/xml': 'xml',
  'text/xml': 'xml',
}

/** Strip parameters (`; charset=...`) en normaliseer naar lowercase. */
export function cleanMimeType(raw: string | null | undefined): string {
  return (raw ?? '').split(';')[0]?.trim().toLowerCase() ?? ''
}

/** Leidt een mime-type af uit de bestandsnaam-extensie (best-effort). */
export function mimeFromFileName(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? ''
  return EXT_MIME[ext] ?? ''
}

export function documentKindFromMime(type: string): DocumentKind {
  if (type.startsWith('image/')) return 'image'
  if (type === 'application/pdf') return 'pdf'
  return 'other'
}

/**
 * Zorgt dat de download-bestandsnaam een extensie heeft. Sommige call-sites
 * geven een kale titel mee (geen extensie); zonder extensie weet het OS niet
 * wat voor bestand het is. Heeft de naam al een extensie, dan blijft die.
 */
export function ensureFileExtension(name: string, type: string): string {
  if (/\.[a-z0-9]{2,5}$/i.test(name)) return name
  const ext = TYPE_EXT[type]
  return ext ? `${name}.${ext}` : name
}

/**
 * Draait de app als geïnstalleerde PWA (geen browser-chrome, dus geen
 * werkende `<a download>`)? Dan is Web Share de enige opslag-route.
 */
export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

export interface FetchedDocument {
  blob: Blob
  /** Genormaliseerd mime-type (uit Content-Type, hint of bestandsnaam). */
  type: string
}

/** Fout bij het ophalen van een document via de proxy-route. */
export class DocumentFetchError extends Error {
  constructor(public readonly status: number) {
    super(`Document ophalen mislukt (status ${status})`)
    this.name = 'DocumentFetchError'
  }
}

/**
 * Haal een document op via de same-origin proxy-route. De Auth.js-sessiecookie
 * reist mee (`credentials: 'include'`); de proxy zet de Bearer-token server-side.
 */
export async function fetchDocumentBlob(
  url: string,
  opts?: { signal?: AbortSignal; fileName?: string; mimeType?: string },
): Promise<FetchedDocument> {
  const res = await fetch(url, { credentials: 'include', signal: opts?.signal })
  if (!res.ok) {
    throw new DocumentFetchError(res.status)
  }
  const blob = await res.blob()
  const type =
    cleanMimeType(res.headers.get('content-type')) ||
    cleanMimeType(opts?.mimeType) ||
    (opts?.fileName ? mimeFromFileName(opts.fileName) : '') ||
    'application/octet-stream'
  return { blob, type }
}

/**
 * Schrijf een blob direct naar schijf via een tijdelijke `<a download>` - geen
 * tussenvenster/viewer. De object-URL wordt na een ruime marge vrijgegeven
 * (te vroeg vrijgeven kan de download in sommige browsers afbreken).
 */
export function triggerBlobDownload(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = fileName
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000)
}

/**
 * Haal het document op en download het direct. Combineert fetch + download zodat
 * call-sites één promise hebben (met nette foutafhandeling).
 */
export async function downloadDocument(
  url: string,
  fileName: string,
  opts?: { mimeType?: string },
): Promise<void> {
  const { blob, type } = await fetchDocumentBlob(url, { fileName, mimeType: opts?.mimeType })
  triggerBlobDownload(blob, ensureFileExtension(fileName, type))
}
